import type { JSX } from 'react';
import { backlogClaim, type CoverageFact } from '../coverage';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * The header's backlog claim, demoted to a chip (ux-spec §6 row 4): the chip's own words already
 * say how much is unread, and the full sentence is one click away rather than one hover away.
 */
export function BacklogChip({
  pending,
  coverage,
}: {
  pending: number;
  coverage: CoverageFact;
}): JSX.Element | null {
  const claim = backlogClaim(pending, coverage);
  if (claim === null) return null;

  return (
    <Popover>
      <PopoverTrigger
        type="button"
        // The `before` box gives the short chip a 44px-tall tap target without a 44px-tall chip.
        className="relative inline-flex items-center gap-1.5 rounded-full border border-caution-line bg-caution-wash py-1 pr-2.5 pl-1.5 text-caption font-semibold text-caution-ink before:absolute before:inset-x-0 before:top-1/2 before:h-11 before:-translate-y-1/2 before:content-[''] hover:border-caution focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:outline-none"
      >
        {/* Hazard stripe: caution semantics only, which a backlog is (spec ui-direction §8.2). */}
        <span
          aria-hidden="true"
          className="size-2.5 shrink-0 rounded-full bg-[repeating-linear-gradient(-45deg,var(--color-caution)_0_2.5px,var(--color-ink)_2.5px_5px)]"
        />
        {claim.short}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-auto max-w-[22rem] p-3 text-small leading-normal text-ink-secondary shadow-pop"
      >
        {claim.full}
      </PopoverContent>
    </Popover>
  );
}
